/**
 * `useComposerHistoryStore` — the last 10 prompts SENT from a chat
 * composer, per conversation scope, so ArrowUp in the promptbox can
 * repeat recent commands (operator request 2026-08-17; plan:
 * `ai-docs/lexide-promptbox-history-nav-miniplan.md`).
 *
 * Scope keys are the conversation identity the mounting surface passes
 * to `ChatInput` (`historyScope` prop): a `caseId` for the case chat,
 * the literal `"global"` for the home/global thread. ⚖ D-2 (operator,
 * 2026-08-17): histories are PER CONVERSATION — one case's commands are
 * noise in another, and cross-case prompt text surfacing in the wrong
 * matter's composer is a confidentiality-adjacent wrong, not just UX.
 *
 * Persistence (⚖ D-1, operator 2026-08-17): **localStorage**, wrapped in
 * `createTenantScopedStorage` — the wrapper is what keeps one browser's
 * users from reading each other's prompts after a re-login on a shared
 * machine (see that module's docblock for the measured incident). Cap
 * 10 strings per scope bounds what sits at rest.
 *
 * Navigation state (index, saved draft) deliberately does NOT live
 * here — it is per-mounted-composer UI state, owned by `ChatInput`.
 * This store is only the durable list.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createTenantScopedStorage } from "@/lib/tenant-scoped-storage";
import { peekTenantKey } from "@/lib/lexiun-jwt";
import { HISTORY_CAP, pushPrompt } from "@/lib/workspace/composer-history";

/** Stable empty list — a selector returning a fresh `[]` per call would
 *  re-render every subscriber on every store change. */
export const EMPTY_HISTORY: readonly string[] = Object.freeze([]);

interface ComposerHistoryStore {
  /** scopeKey → MRU prompt list, newest first, ≤ HISTORY_CAP each. */
  history: Record<string, string[]>;
  /** Record a SENT prompt (reducer trims / skips blanks / collapses a
   *  repeat of the newest entry / caps). */
  recordPrompt: (scopeKey: string, text: string) => void;
  clear: (scopeKey: string) => void;
}

export const useComposerHistoryStore = create<ComposerHistoryStore>()(
  persist(
    (set) => ({
      history: {},
      recordPrompt: (scopeKey, text) =>
        set((state) => {
          const next = pushPrompt(state.history[scopeKey] ?? [], text, HISTORY_CAP);
          // The reducer returns an equivalent list for no-op pushes
          // (blank / repeat-of-head); writing it anyway would churn
          // persist + subscribers for nothing.
          const prev = state.history[scopeKey] ?? [];
          if (next.length === prev.length && next.every((v, i) => v === prev[i])) {
            return state;
          }
          return { history: { ...state.history, [scopeKey]: next } };
        }),
      clear: (scopeKey) =>
        set((state) => {
          if (!(scopeKey in state.history)) return state;
          const { [scopeKey]: _dropped, ...rest } = state.history;
          return { history: rest };
        }),
    }),
    {
      name: "lexide:composer-history",
      // localStorage (not session): "repeat last commands" is most
      // valuable across days — ⚖ D-1. Tenant scoping is NOT optional
      // here; prompts are case text.
      storage: createJSONStorage(() => createTenantScopedStorage(() => localStorage)),
      partialize: (state) => ({ history: state.history }),
      // ⚠ LOAD-BEARING, measured live 2026-08-17: default hydration runs
      // at module load, BEFORE auth resolves — `peekTenantKey()` is null
      // there, and the tenant wrapper (correctly, fail-closed) refuses
      // the payload AND DELETES IT. For the other tenant-scoped stores
      // that is the documented cost of a cache: one refetch. This store
      // is NOT a cache — the localStorage copy is the ONLY copy — so
      // eager hydration silently destroyed the whole history on every
      // reload. Hydration is therefore deferred until identity is known
      // (`ensureComposerHistoryHydrated`, called by the composer once
      // the gateway is connected — connected ⇒ auth resolved ⇒ the
      // stamp check can actually pass).
      skipHydration: true,
    },
  ),
);

let hydratedFor: string | null = null;

/** Test seam only — resets the module guard so each test starts cold. */
export function __resetComposerHistoryHydrationForTest(): void {
  hydratedFor = null;
}

/**
 * Hydrate once identity is readable — PER TENANT, not merely once.
 * Idempotent; safe to call from every composer mount. Retries
 * implicitly: while `peekTenantKey()` is still null nothing happens and
 * the guard stays unset, so a later call (next render after auth
 * resolves) does the real read.
 *
 * ⚠ The guard is keyed on the TENANT, not a boolean, and that is the
 * store's own contract applied to MEMORY: the storage wrapper refuses a
 * foreign payload on read, but it cannot reach state already hydrated
 * into this module — so an identity change WITHOUT a reload (sign-out
 * navigates, but "sign-out is not the only way identity changes" —
 * `App.tsx`'s own doctrine at the `clearTenantScopedCaches` call) must
 * drop the previous tenant's prompts before reading the new tenant's.
 * A boolean once-guard here would have rendered lawyer A's prompt
 * history inside lawyer B's composer.
 *
 * Within ONE tenant the guard still prevents the re-hydrate-after-write
 * hazard: a second rehydrate would overwrite in-memory prompts with the
 * older disk snapshot.
 */
export function ensureComposerHistoryHydrated(): void {
  const current = peekTenantKey();
  if (current === null) return;
  if (hydratedFor === current) return;
  if (hydratedFor !== null) {
    // Identity CHANGED since the last hydration — drop the previous
    // tenant's in-memory history before reading. The read below serves
    // only payloads stamped for `current` (wrapper-enforced), so after
    // this pair the state is provably single-tenant.
    useComposerHistoryStore.setState({ history: {} });
  }
  hydratedFor = current;
  void useComposerHistoryStore.persist.rehydrate();
}
